import { useEffect, useMemo, useRef, useState } from "react";
import { X, Volume2 } from "lucide-react";
import { img } from "../../lib/api";

// Extract 11-char YouTube video ID from the common URL shapes.
function youTubeId(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.replace(/^\//, "").slice(0, 11);
    if (u.searchParams.get("v")) return u.searchParams.get("v").slice(0, 11);
    const m = u.pathname.match(/\/(embed|shorts)\/([^/?#]+)/);
    if (m) return m[2].slice(0, 11);
  } catch { /* fallthrough */ }
  const m = String(url).match(/[A-Za-z0-9_-]{11}/);
  return m ? m[0] : "";
}

// Minimal YouTube embed: autoplay-muted + overlay to hide YouTube chrome
// (title, channel, CC, settings, logo, center play button). A single
// "🔊 Ses aç" button unmutes via postMessage once the user interacts.
function YouTubeEmbed({ videoId, params, title }) {
  const iframeRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const unmute = () => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    // YouTube IFrame Player API — JSON command messaging.
    const send = (func, args = "") => win.postMessage(
      JSON.stringify({ event: "command", func, args }),
      "*"
    );
    send("unMute");
    send("setVolume", [100]);
    send("playVideo");
    setMuted(false);
  };

  return (
    <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
      <iframe
        ref={iframeRef}
        src={`https://www.youtube.com/embed/${videoId}?${params}`}
        title={title}
        className="absolute inset-0 w-full h-full border-0"
        allow="autoplay; encrypted-media; picture-in-picture"
      />
      {/* Chrome-maske: iframe'in üstünde ÇOĞU tıklamayı yakalar — user'ın
          YouTube'un kendi UI'ına erişmesini engeller. Sadece "ses aç"
          butonu interaktif kalır (sağ-alt köşe). */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
      />
      {muted && (
        <button
          type="button"
          onClick={unmute}
          data-testid="promo-yt-unmute"
          className="absolute bottom-3 right-3 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-black/80 hover:bg-black text-white text-xs font-semibold shadow-lg backdrop-blur-sm transition"
          aria-label="Sesi aç"
        >
          <Volume2 size={14} />
          Sesi aç
        </button>
      )}
    </div>
  );
}

const SIZES = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
};

const POSITIONS = {
  "center": "items-center justify-center",
  "bottom-right": "items-end justify-end",
  "bottom-left": "items-end justify-start",
};

/**
 * Public promo modal.
 * `forcePreview` bypasses the frequency gate — admins hit this from PromoAdmin.
 */
export default function PromoModal({ promo, forcePreview = false, onClose }) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  // Compose a stable per-config key so changing any field resets dismissals.
  const dismissKey = useMemo(() => `promo:${promo?.version || "x"}`, [promo?.version]);

  useEffect(() => {
    if (!promo?.enabled && !forcePreview) return undefined;
    if (forcePreview) {
      setOpen(true);
      return undefined;
    }

    // Frequency gate (client-side)
    const freq = promo.frequency || "session";
    if (freq === "session" && sessionStorage.getItem(dismissKey)) return undefined;
    if (freq === "days") {
      const raw = localStorage.getItem(dismissKey);
      if (raw) {
        const until = Number(raw) || 0;
        if (until > Date.now()) return undefined;
      }
    }

    const delay = Math.max(0, Number(promo.delay_seconds) || 0) * 1000;
    timerRef.current = setTimeout(() => setOpen(true), delay);
    return () => clearTimeout(timerRef.current);
  }, [promo, forcePreview, dismissKey]);

  if (!open) return null;

  const close = (dontShowAgain = false) => {
    const freq = promo.frequency || "session";
    if (!forcePreview) {
      if (dontShowAgain || freq === "days") {
        const days = Math.max(1, Number(promo.frequency_days) || 7);
        localStorage.setItem(dismissKey, String(Date.now() + days * 86400000));
      } else if (freq === "session") {
        sessionStorage.setItem(dismissKey, "1");
      }
    }
    setOpen(false);
    onClose && onClose();
  };

  const size = SIZES[promo.size] || SIZES.md;
  const position = POSITIONS[promo.position] || POSITIONS.center;
  const yt = promo.media_type === "youtube" ? youTubeId(promo.youtube_url) : "";
  // YouTube IFrame params: controls=0 kaldırır alt bar + büyük play/pause,
  // modestbranding + showinfo + iv_load_policy=3 marka/başlık/annotation'ı
  // bastırır (YouTube bazı chrome öğelerini gene de buffer sırasında
  // gösterir — tam yok etmek postMessage API ile mümkün değil, sadece
  // overlay ile maskeleyebiliriz).
  const ytParams = new URLSearchParams({
    autoplay: "1",
    mute: "1",              // tarayıcı otomatik oynatma politikası için ŞART
    playsinline: "1",
    rel: "0",
    modestbranding: "1",
    showinfo: "0",
    iv_load_policy: "3",
    controls: "0",
    disablekb: "1",
    fs: "0",
    cc_load_policy: "0",
    enablejsapi: "1",       // postMessage ile unMute komutu için
    origin: typeof window !== "undefined" ? window.location.origin : "",
  }).toString();

  const hasImg = promo.media_type === "image" && promo.image_url;
  const hasVid = promo.media_type === "video" && promo.video_url;

  const image = (
    <img
      src={img(promo.image_url)}
      alt={promo.image_alt || promo.headline || ""}
      className="w-full h-auto block"
      loading="eager"
      decoding="async"
    />
  );

  return (
    <div
      className={`fixed inset-0 z-[100] flex ${position} p-3 sm:p-6 bg-black/60 backdrop-blur-sm`}
      role="dialog"
      aria-modal="true"
      aria-label={promo.headline || "Reklam"}
      data-testid="promo-modal"
      onClick={() => close(false)}
    >
      <div
        className={`relative w-full ${size} bg-white rounded-xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => close(false)}
          aria-label="Kapat"
          data-testid="promo-close"
          className="absolute top-2 right-2 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white border flex items-center justify-center shadow"
        >
          <X size={16} />
        </button>

        {hasImg && (
          promo.image_link
            ? <a href={promo.image_link} target="_blank" rel="noopener noreferrer" data-testid="promo-image-link">{image}</a>
            : image
        )}

        {promo.media_type === "youtube" && yt && (
          <YouTubeEmbed videoId={yt} params={ytParams} title={promo.headline || "YouTube"} />
        )}

        {hasVid && (
          <video
            src={promo.video_url}
            autoPlay={!!promo.autoplay}
            muted={!!promo.muted}
            loop={!!promo.loop}
            playsInline
            controls
            className="w-full h-auto block bg-black"
          />
        )}

        {(promo.headline || promo.description || promo.cta_enabled || promo.show_dont_show_again) && (() => {
          const buttons = (promo.buttons || []).filter((b) => b && b.enabled && b.text && b.url);
          // Legacy single-button fallback: if admin never configured the new
          // `buttons` list but turned `cta_enabled` on, honour the old config.
          const legacy = buttons.length === 0 && promo.cta_enabled && promo.cta_url ? [{
            text: promo.cta_text || "Devamı",
            url: promo.cta_url,
            color: promo.cta_color || "#14213D",
            text_color: promo.cta_text_color || "#FFFFFF",
          }] : [];
          const list = buttons.length ? buttons : legacy;
          const alignRow = { left: "justify-start", center: "justify-center", right: "justify-end" }[promo.cta_align || "left"] || "justify-start";
          return (
            <div
              className="p-4 sm:p-5 space-y-3"
              style={{
                backgroundColor: promo.panel_bg_color || "#FFFFFF",
                color: promo.panel_text_color || "#1C1917",
              }}
            >
              {promo.headline && (
                <h3 className="text-lg sm:text-xl font-semibold font-serif" data-testid="promo-headline-out">{promo.headline}</h3>
              )}
              {promo.description && (
                <p className="text-sm whitespace-pre-line opacity-90">{promo.description}</p>
              )}
              {promo.cta_enabled && list.length > 0 && (
                <div className={`flex flex-col sm:flex-row flex-wrap gap-2 sm:items-center ${alignRow}`} data-testid="promo-cta-row">
                  {list.map((b, i) => (
                    <a
                      key={i}
                      href={b.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => close(false)}
                      className="inline-block w-full sm:w-auto text-center px-5 py-2.5 rounded-md text-sm font-medium shadow-sm"
                      style={{ backgroundColor: b.color || "#14213D", color: b.text_color || "#FFFFFF" }}
                      data-testid={`promo-cta-${i}`}
                    >
                      {b.text || "Devamı"}
                    </a>
                  ))}
                </div>
              )}
              {promo.show_dont_show_again && !forcePreview && (
                <button
                  type="button"
                  onClick={() => close(true)}
                  className="block text-xs opacity-70 hover:opacity-100 underline underline-offset-2"
                  data-testid="promo-dsa-btn"
                >Bir daha gösterme</button>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
